// Deterministic demo household data (6 months: checking, savings, credit card)
import { defaultCategories } from "./categories";
import { categorize } from "./categorize";
import { toISO, round2 } from "./format";

function rng(seed) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

export function sampleData(monthsBack = 6) {
  const r = rng(42);
  const cats = defaultCategories();
  const accounts = [
    { id: "acc-chk", name: "Everyday Checking", type: "checking", institution: "Sample Community Bank", last4: "4821", balance: null },
    { id: "acc-sav", name: "Rainy Day Savings", type: "savings", institution: "Sample Community Bank", last4: "9930", balance: 6200 },
    { id: "acc-cc", name: "Rewards Visa", type: "credit", institution: "Sample Card Co", last4: "1177", balance: null },
  ];
  const members = [
    { id: "m1", name: "Household" },
    { id: "m2", name: "Alex" },
    { id: "m3", name: "Jordan" },
  ];
  const tx = [];
  const add = (accountId, date, description, amount, extra = {}) => tx.push({ accountId, date, description, amount: round2(amount), memberId: "m1", scope: "shared", ...extra });
  const now = new Date();
  for (let i = monthsBack; i >= 1; i--) {
    const y = new Date(now.getFullYear(), now.getMonth() - i, 1).getFullYear();
    const m = new Date(now.getFullYear(), now.getMonth() - i, 1).getMonth();
    const d = (day) => toISO(new Date(y, m, day));
    add("acc-chk", d(1), "ACME CORP PAYROLL DIRECT DEP", 2875.4, { memberId: "m2", scope: "individual" });
    add("acc-chk", d(15), "ACME CORP PAYROLL DIRECT DEP", 2875.4, { memberId: "m2", scope: "individual" });
    add("acc-chk", d(5), "RIVERSIDE SCHOOL DIST PAYROLL", 1960.0, { memberId: "m3", scope: "individual" });
    add("acc-chk", d(20), "RIVERSIDE SCHOOL DIST PAYROLL", 1960.0, { memberId: "m3", scope: "individual" });
    add("acc-chk", d(3), "ROCKET MORTGAGE MTG PMT", -1845.0);
    add("acc-chk", d(8), "DUKE ENERGY ELECTRIC BILL", -(118 + r() * 60 + (m === 6 || m === 7 ? 70 : 0)));
    add("acc-chk", d(12), "CITY OF SPRINGFIELD WATER UTIL", -(52 + r() * 18));
    add("acc-chk", d(10), "COMCAST XFINITY INTERNET", i <= 2 ? -89.99 : -79.99);
    add("acc-chk", d(18), "VERIZON WIRELESS BILL PAY", -142.5);
    add("acc-chk", d(22), "GEICO AUTO INS", -168.0);
    add("acc-chk", d(25), "TOYOTA FINANCIAL AUTO PMT", -412.0);
    add("acc-chk", d(27), "NAVIENT STUDENT LOAN", -265.0);
    add("acc-chk", d(16), "ONLINE TRANSFER TO SAVINGS", -400.0, { category: "emergency-fund" });
    add("acc-sav", d(16), "ONLINE TRANSFER FROM CHECKING", 400.0);
    add("acc-chk", d(28), "PAYMENT TO REWARDS VISA CREDIT CARD PAYMENT", -1400.0);
    add("acc-cc", d(28), "PAYMENT THANK YOU", 1400.0);
    if (i % 2 === 0) add("acc-chk", d(9), "MONTHLY MAINTENANCE FEE", -12.0);
    if (i === 3) add("acc-chk", d(14), "OVERDRAFT FEE", -35.0);
    add("acc-chk", d(6), "ATM WITHDRAWAL 1234 MAIN ST", -100.0);
    add("acc-cc", d(27), "INTEREST CHARGE ON PURCHASES", -(48 * 1.12 ** (monthsBack - i)));
    add("acc-cc", d(4), "NETFLIX.COM", -15.49);
    add("acc-cc", d(7), "SPOTIFY USA", -11.99);
    add("acc-cc", d(11), "HULU 877-8248", -17.99);
    add("acc-cc", d(13), "PLANET FITNESS MONTHLY", -24.99);
    for (let w = 0; w < 4; w++) {
      add("acc-cc", d(2 + w * 7), "KROGER #1123 GROCERY", -(115 + r() * 70));
      add("acc-cc", d(4 + w * 7), "SHELL OIL 57442", -(38 + r() * 22));
      add("acc-cc", d(5 + w * 7), "STARBUCKS STORE 0921", -(6 + r() * 5));
      add("acc-cc", d(6 + w * 7), w % 2 ? "OLIVE GARDEN 0441" : "DOORDASH*THAI KITCHEN", -(38 + r() * 45));
      if (w % 2) add("acc-cc", d(3 + w * 7), "CHIPOTLE 1882", -(14 + r() * 12));
    }
    add("acc-cc", d(19), "TARGET T-1442", -(60 + r() * 90));
    add("acc-cc", d(21), "CVS/PHARMACY #0442", -(18 + r() * 30));
    add("acc-cc", d(23), "CHEWY.COM", -(45 + r() * 20));
    if (i === 2) add("acc-cc", d(17), "DELTA AIR LINES", -684.2);
    add("acc-chk", d(9), "RED CROSS DONATION", -50);
    if (i % 3 === 0) add("acc-cc", d(14), "BRIGHT SMILES DENTAL", -(120 + i * 10));
    if (i % 2 === 1) add("acc-cc", d(18), "STAPLES OFFICE SUPPLIES", -(40 + i * 7), { business: true, notes: "Home office supplies", category: "one-time-expenses" });
    if (i === 4) add("acc-cc", d(24), "DISCOUNT TIRE #882", -742.55);
    if (i === 1) add("acc-cc", d(26), "TARGET T-1442 REFUND", 42.1);
  }
  const transactions = tx.map((t, i) => {
    const res = t.category ? { category: t.category, confident: true } : categorize(t, [], cats);
    return { id: `s${i}`, source: "sample", flagged: false, flagReason: "", notes: "", excluded: false, business: false, ...t, category: res.category, autoCategory: true };
  });
  accounts[0].balance = 3850.27;
  return {
    accounts,
    members,
    transactions,
    debts: [
      { id: "d1", creditor: "Rewards Visa", balance: 3200, rate: 22.9, minPayment: 95, dueDay: 28 },
      { id: "d2", creditor: "Toyota Financial (auto loan)", balance: 14800, rate: 6.4, minPayment: 412, dueDay: 25 },
      { id: "d3", creditor: "Navient (student loan)", balance: 21500, rate: 5.1, minPayment: 265, dueDay: 27 },
    ],
    goals: [
      { id: "g1", name: "Emergency fund", type: "Emergency fund", target: 15000, current: 6200, monthly: 400, deadline: "" },
      { id: "g2", name: "Summer vacation", type: "Vacation", target: 3000, current: 750, monthly: 150, deadline: "" },
    ],
  };
}
